// @ts-strict-ignore
import {
  dateCell,
  moneyCell,
  pillCell,
  readonlyTextCell,
  textCell,
} from "@dashboard/components/Datagrid/customCells/cells";
import { GetCellContentOpts } from "@dashboard/components/Datagrid/Datagrid";
import { AvailableColumn } from "@dashboard/components/Datagrid/types";
import {
  OrderChargeStatusEnum,
  OrderListQuery,
  OrderStatus,
  PaymentChargeStatusEnum,
} from "@dashboard/graphql";
import { commonStatusMessages, orderStatusMessages } from "@dashboard/intl";
import {
  getStatusColor,
  PillStatusType,
  transformChargedStatus,
  transformPaymentStatus,
} from "@dashboard/misc";
import { orderProgressStepperMessages } from "@dashboard/orders/components/OrderProgressStepper/messages";
import { isReceivedManuallyMarked } from "@dashboard/orders/components/OrderProgressStepper/utils";
import { OrderListUrlSortField } from "@dashboard/orders/urls";
import { RelayToFlat, Sort } from "@dashboard/types";
import { getColumnSortDirectionIcon } from "@dashboard/utils/columns/getColumnSortDirectionIcon";
import { GridCell, Item, TextCell } from "@glideapps/glide-data-grid";
import { DefaultTheme, useTheme } from "@saleor/macaw-ui-next";
import { IntlShape, useIntl } from "react-intl";

import { columnsMessages, messages } from "./messages";

export const orderListStaticColumnAdapter = (
  emptyColumn: AvailableColumn,
  intl: IntlShape,
  sort: Sort<OrderListUrlSortField>,
) =>
  [
    emptyColumn,
    {
      id: "number",
      title: intl.formatMessage(columnsMessages.number),
      width: 100,
    },
    {
      id: "date",
      title: intl.formatMessage(columnsMessages.date),
      width: 300,
    },
    {
      id: "customer",
      title: intl.formatMessage(columnsMessages.customer),
      width: 200,
    },
    {
      id: "payment",
      title: intl.formatMessage(columnsMessages.payment),
      width: 200,
    },
    {
      id: "status",
      title: intl.formatMessage(columnsMessages.status),
      width: 200,
    },
    {
      id: "total",
      title: intl.formatMessage(columnsMessages.total),
      width: 150,
    },
    {
      id: "channel",
      title: intl.formatMessage(columnsMessages.channel),
      width: 200,
    },
  ].map(column => ({
    ...column,
    icon: getColumnSortDirectionIcon(sort, column.id),
  }));

interface GetCellContentProps {
  columns: AvailableColumn[];
  orders: RelayToFlat<OrderListQuery["orders"]>;
}

function getDatagridRowDataIndex(row, removeArray) {
  return row + removeArray.filter(r => r <= row).length;
}

export const useGetCellContent = ({ columns, orders }: GetCellContentProps) => {
  const intl = useIntl();
  const { theme } = useTheme();

  return ([column, row]: Item, { added, removed }: GetCellContentOpts): GridCell => {
    const columnId = columns[column]?.id;
    const rowData = added.includes(row) ? undefined : orders[getDatagridRowDataIndex(row, removed)];

    if (!columnId || !rowData) {
      return readonlyTextCell("");
    }

    switch (columnId) {
      case "number":
        return readonlyTextCell(rowData.number);
      case "date":
        return getDateCellContent(rowData);
      case "customer":
        return getCustomerCellContent(rowData);
      case "payment":
        return getPaymentCellContent(intl, theme, rowData);
      case "status":
        return getStatusCellContent(intl, theme, rowData);
      case "total":
        return getTotalCellContent(rowData);
      case "channel":
        return getChannelCellContent(rowData);
      default:
        return textCell("");
    }
  };
};

const COMMON_CELL_PROPS: Partial<GridCell> = { cursor: "pointer" };

function getDateCellContent(rowData: RelayToFlat<OrderListQuery["orders"]>[number]) {
  return dateCell(rowData?.created, COMMON_CELL_PROPS);
}

export function getCustomerCellContent(
  rowData: RelayToFlat<OrderListQuery["orders"]>[number],
): TextCell {
  if (rowData?.billingAddress?.firstName && rowData?.billingAddress?.lastName) {
    return readonlyTextCell(
      `${rowData.billingAddress.firstName} ${rowData.billingAddress.lastName}`,
    );
  }

  if (rowData?.userEmail) {
    return readonlyTextCell(rowData.userEmail);
  }

  return readonlyTextCell("-");
}

function getStatusCellContent(
  intl: IntlShape,
  currentTheme: DefaultTheme,
  rowData: RelayToFlat<OrderListQuery["orders"]>[number],
) {
  const stage = getOrderProgressStage(rowData, intl);

  if (stage) {
    const color = getStatusColor({
      status: stage.status,
      currentTheme,
    });

    return pillCell(stage.localized, color, COMMON_CELL_PROPS);
  }

  return readonlyTextCell("-");
}

// The order-list status column shows where an order is in its journey rather than
// only a binary fulfilled/unfulfilled. It reads as a status (past tense — "Shipped",
// "Completed") to match the order-detail status chip, and reflects the manual
// "received" mark (fetched as order metadata) as the terminal "Completed" state.
export function getOrderProgressStage(
  rowData: RelayToFlat<OrderListQuery["orders"]>[number],
  intl: IntlShape,
): { localized: string; status: PillStatusType } | null {
  if (!rowData?.status) {
    return null;
  }

  const isPaid = rowData.paymentStatus === PaymentChargeStatusEnum.FULLY_CHARGED;

  // A confirmed receipt is the true terminal state, regardless of the native
  // fulfillment status. It's stored as an order metadata flag (fetched by the list
  // query) and mirrors the order-detail "Completed" chip.
  if (isReceivedManuallyMarked(rowData as Parameters<typeof isReceivedManuallyMarked>[0])) {
    return {
      localized: intl.formatMessage(orderStatusMessages.completed),
      status: "success",
    };
  }

  switch (rowData.status) {
    // Terminal / non-linear states keep their own meaning and colour.
    case OrderStatus.CANCELED:
      return {
        localized: intl.formatMessage(commonStatusMessages.cancelled),
        // Neutral grey: cancelled orders are inactive, not an error to act on.
        status: "neutral",
      };
    case OrderStatus.EXPIRED:
      return {
        localized: intl.formatMessage(orderStatusMessages.expired),
        status: "error",
      };
    case OrderStatus.RETURNED:
      return {
        localized: intl.formatMessage(orderStatusMessages.returned),
        status: "info",
      };
    case OrderStatus.PARTIALLY_RETURNED:
      return {
        localized: intl.formatMessage(orderStatusMessages.partiallyReturned),
        status: "info",
      };
    case OrderStatus.DRAFT:
      return {
        localized: intl.formatMessage(orderStatusMessages.draft),
        status: "info",
      };
    // A FULFILLED order is packed/dispatched ("Shipped"), not necessarily received.
    // Receipt is confirmed via the manual mark handled above; until then the list
    // shows the last provable state as a status rather than the stepper's "Shipping"
    // step noun.
    case OrderStatus.FULFILLED:
      return {
        localized: intl.formatMessage(orderStatusMessages.fulfilled),
        status: "info",
      };
    // The first step: an unconfirmed order still needs staff to confirm it.
    case OrderStatus.UNCONFIRMED:
      return {
        localized: intl.formatMessage(orderProgressStepperMessages.submitted),
        status: "warning",
      };
    default:
      break;
  }

  // Remaining in-progress states (UNFULFILLED / PARTIALLY_FULFILLED) advance
  // through the payment → preparation → shipping steps.
  if (!isPaid) {
    return {
      localized: intl.formatMessage(messages.unpaid),
      status: "warning",
    };
  }

  if (rowData.status === OrderStatus.PARTIALLY_FULFILLED) {
    return {
      localized: intl.formatMessage(orderStatusMessages.partiallyFulfilled),
      status: "info",
    };
  }

  // Purple so Preparation is visually distinct from the blue "Shipped"/"Partially
  // fulfilled" stages that follow it.
  return {
    localized: intl.formatMessage(orderProgressStepperMessages.preparation),
    status: "purple",
  };
}

const higherPriorityChargeStatuses = [OrderChargeStatusEnum.OVERCHARGED];

// Payment pill shown in the order list; shared by the datagrid and the mobile list.
export function getOrderPaymentStage(
  rowData: RelayToFlat<OrderListQuery["orders"]>[number],
  intl: IntlShape,
): { localized: string; status: PillStatusType } | null {
  if (higherPriorityChargeStatuses.includes(rowData.chargeStatus)) {
    return transformChargedStatus(rowData.chargeStatus, intl);
  }

  const paymentStatus = transformPaymentStatus(rowData.paymentStatus, intl);

  if (!paymentStatus) {
    return null;
  }

  return {
    localized: paymentStatus.localized,
    // Cancelled payments are neutral/terminal, not an error to act on.
    status:
      rowData.paymentStatus === PaymentChargeStatusEnum.CANCELLED
        ? "neutral"
        : paymentStatus.status,
  };
}

export function getPaymentCellContent(
  intl: IntlShape,
  currentTheme: DefaultTheme,
  rowData: RelayToFlat<OrderListQuery["orders"]>[number],
) {
  const stage = getOrderPaymentStage(rowData, intl);

  if (!stage) {
    return readonlyTextCell("-");
  }

  const color = getStatusColor({
    status: stage.status,
    currentTheme,
  });

  return pillCell(stage.localized, color, COMMON_CELL_PROPS);
}

function getTotalCellContent(rowData: RelayToFlat<OrderListQuery["orders"]>[number]) {
  if (rowData?.total?.gross) {
    return moneyCell(rowData.total.gross.amount, rowData.total.gross.currency, COMMON_CELL_PROPS);
  }

  return readonlyTextCell("-");
}

function getChannelCellContent(rowData: RelayToFlat<OrderListQuery["orders"]>[number]): TextCell {
  if (rowData?.channel?.name) {
    return readonlyTextCell(rowData.channel.name);
  }

  return readonlyTextCell("-");
}
